import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import { splitPromptAndFences } from "@/components/storyboard/code/codeUtils";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import QuizReviewQuestion from "@/components/storyboard/quiz/review/QuizReviewQuestion";
import QuizReviewOptionList from "@/components/storyboard/quiz/review/QuizReviewOptionList";
import QuizReviewCloze from "@/components/storyboard/quiz/review/QuizReviewCloze";
import QuizReviewMatching from "@/components/storyboard/quiz/review/QuizReviewMatching";
import QuizReviewText from "@/components/storyboard/quiz/review/QuizReviewText";
import QuizReviewCode from "@/components/storyboard/quiz/review/QuizReviewCode";
import QuizReviewReveal from "@/components/storyboard/quiz/review/QuizReviewReveal";

interface QuizReviewListProps {
  review: QuizReview;
}

const renderBody = (item: QuizReviewItem) => {
  switch (item.kind) {
    case "options":
      return (
        <QuizReviewOptionList
          options={item.options}
          codeSnippet={item.codeSnippet}
        />
      );
    case "cloze":
      return (
        <QuizReviewCloze
          clozeText={item.clozeText}
          gapMark={item.gapMark}
          gaps={item.gaps}
          isCorrect={item.isCorrect}
        />
      );
    case "matching":
      return <QuizReviewMatching rows={item.rows} />;
    case "text":
      return <QuizReviewText userAnswer={item.userAnswer} />;
    case "code":
      return (
        <QuizReviewCode userAnswer={item.userAnswer} language={item.language} />
      );
    case "reveal":
      return <QuizReviewReveal blocks={item.blocks} />;
  }
};

const renderReviewFeedback = (item: QuizReviewItem) => {
  if (!("isCorrect" in item) || !item.feedback) return null;

  const { isCorrect, feedback } = item;
  const positiveFeedback = isCorrect ? feedback : undefined;
  const negativeFeedback = isCorrect ? undefined : feedback;

  return (
    <QuizFeedback
      isSubmitted
      isCorrect={isCorrect}
      positiveFeedback={positiveFeedback}
      negativeFeedback={negativeFeedback}
      className="mt-3 mb-0 w-full"
    />
  );
};

const renderScore = (correct: number, total: number, noun: string) => {
  if (total === 0) return null;

  return (
    <span className="inline-flex items-center rounded-full border border-info-border bg-info-light px-3 py-1 text-info-foreground">
      {correct}/{total} {noun} richtig
    </span>
  );
};

const optionsReviewWithSnippetBesidePrompt = (
  item: QuizReviewItem
): QuizReviewItem => {
  if (item.kind !== "options") return item;

  const { prompt, remainder } = splitPromptAndFences(item.prompt);
  return {
    ...item,
    prompt,
    codeSnippet: item.codeSnippet ?? remainder ?? undefined,
  };
};

const QuizReviewList = ({ review }: QuizReviewListProps) => {
  // A quiz resumed halfway only records the questions answered in that
  // session, so the stored array has holes — JSON turns them into nulls. Skip
  // them rather than taking the whole page down with the recap.
  const items = (review.items ?? [])
    .filter((item): item is QuizReviewItem => item != null)
    .map(optionsReviewWithSnippetBesidePrompt);
  if (items.length === 0) return null;

  const scored = items.filter(
    (item): item is Exclude<QuizReviewItem, { kind: "reveal" }> =>
      item.kind !== "reveal"
  );
  const total = scored.length;
  const correct = scored.filter((item) => item.isCorrect).length;
  // Code quizzes pose Aufgaben, the rest ask Fragen — keep the score pill in
  // the same words as the counters above each item.
  const scoreNoun = scored.every((item) => item.kind === "code")
    ? "Aufgaben"
    : "Fragen";

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2 pl-3 text-base font-semibold">
        <p className="text-primary">Deine Antworten</p>
        {renderScore(correct, total, scoreNoun)}
      </div>
      {items.map((item, index) => (
        <QuizReviewQuestion
          key={index}
          prompt={item.prompt}
          index={index}
          total={items.length}
          counterLabel={
            item.kind === "reveal" || item.kind === "code" ? "Aufgabe" : "Frage"
          }
        >
          {renderBody(item)}
          {renderReviewFeedback(item)}
        </QuizReviewQuestion>
      ))}
    </div>
  );
};

export default QuizReviewList;
